import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";

export default function Login() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const { login, signup } = useApp();
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({ email: "", password: "", name: "", phone: "" });

  const doLogin = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const u = await login(f.email, f.password);
      toast.success(`Welcome back, ${u.name}`);
      nav(u.role === "admin" ? "/admin" : next);
    } catch (err) { toast.error(err.response?.data?.detail || "Login failed"); }
    finally { setLoading(false); }
  };
  const doSignup = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await signup(f.name, f.email, f.password, f.phone);
      toast.success("Welcome to Venvay");
      nav(next);
    } catch (err) { toast.error(err.response?.data?.detail || "Signup failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] grid md:grid-cols-2">
      <div className="hidden md:flex items-center justify-center bg-[#0F0B08] text-[#F0E5D0] p-16 relative overflow-hidden">
        <div className="relative z-10 max-w-md">
          <p className="label-tag text-[#D4B25C]/60 mb-6">Members Only</p>
          <h1 className="font-display text-5xl lg:text-6xl font-black leading-none">
            NOT FOR EVERYONE.<br />JUST FOR <span className="text-[#D4B25C]">YOU.</span>
          </h1>
          <p className="mt-6 text-white/60 italic">Be Rare. Be Venvay.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="mb-8"><BrandLogo /></div>
          <Tabs defaultValue="login">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger data-testid="tab-login" value="login">Login</TabsTrigger>
              <TabsTrigger data-testid="tab-signup" value="signup">Sign Up</TabsTrigger>
            </TabsList>
            <TabsContent value="login">
              <form onSubmit={doLogin} className="space-y-4 mt-6">
                <div><Label>Email</Label><Input data-testid="login-email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
                <div><Label>Password</Label><Input data-testid="login-password" type="password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
                <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-[#8B2E2E] hover:bg-[#6f2323] h-11 uppercase tracking-widest text-xs">
                  {loading ? "Signing in…" : "Sign In"}
                </Button>
              </form>
            </TabsContent>
            <TabsContent value="signup">
              <form onSubmit={doSignup} className="space-y-4 mt-6">
                <div><Label>Full Name</Label><Input data-testid="signup-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
                <div><Label>Email</Label><Input data-testid="signup-email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
                <div><Label>Phone (WhatsApp)</Label><Input data-testid="signup-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></div>
                <div><Label>Password (min 6)</Label><Input data-testid="signup-password" type="password" required minLength={6} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
                <Button data-testid="signup-submit" type="submit" disabled={loading} className="w-full bg-[#8B2E2E] hover:bg-[#6f2323] h-11 uppercase tracking-widest text-xs">
                  {loading ? "Creating account…" : "Create Account"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
          <p className="mt-6 text-xs text-center text-muted-foreground">
            By continuing you agree to our <Link to="/" className="underline">terms</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
